/**
 * Converts a raw shipping response into a structured format for frontend use.
 *
 * Extracts shipping methods, time slots, and price details, returning them in a normalized format.
 *
 * @param {Object} response - Raw shipping response from the backend.
 * @param {Array<Object>} response.data - List of shipping method objects.
 *
 * @returns {Object} Parsed response with:
 * - `data.shippingDetails`: Mapped shipping details keyed by method ID.
 * - `data.keys`: Array of method IDs.
 * - Other metadata: `isCached`, `status`, `responseStatus`.
 */
export function convertShippingResponse(response) {
  const shippingDetails = {};
  const keys = [];

  response?.data?.forEach((item) => {
    const [key, value] = Object.entries(item)[0];

    const { shippingMethodName, timeSlots, priceDetails } = value;

    // Extract the timeSlot and build the desired format
    const formattedTimeSlots = timeSlots?.map((slot) => ({
      timeSlotId: slot?.timeSlotId,
      startTime: slot?.startTime,
      endTime: slot?.endTime,
      shippingPrice: slot?.shippingPrice,
      shipmentMethodId: key,
    }));

    shippingDetails[key] = {
      shippingMethodName,
      timeSlots: formattedTimeSlots,
      earliestDelivery: "", // Not present in response
      productShipmentMethodId: "", // Not present in response
      priceDetails: priceDetails?.price || 0,
    };

    keys.push(key);
  });

  return {
    data: {
      shippingDetails,
      keys,
      message: "success",
      isShippingDetailsExists: true,
      isSuccess: true,
    },
    isCached: response.isCached,
    responseStatus: response.responseStatus,
    status: response.status,
  };
}
